import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../../services/api';
import type { Achievement } from '../../types/api';

export const AdminAchievementsPage: React.FC = () => {
    const { setFlashMessage } = useOutletContext<{ setFlashMessage: (msg: any) => void }>();
    const [achievements, setAchievements] = useState<Achievement[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [editingAch, setEditingAch] = useState<Partial<Achievement> | null>(null);
    const [saving, setSaving] = useState<boolean>(false);

    const loadAchievements = async () => {
        setLoading(true);
        try {
            const res = await api.adminGetAchievements();
            setAchievements(res.data);
        } catch {
            // error handled
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAchievements();
    }, []);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingAch) return;
        setSaving(true);
        try {
            await api.adminSaveAchievement(editingAch.id || null, editingAch);
            setFlashMessage({
                type: 'success',
                text: `Achievement successfully ${editingAch.id ? 'updated' : 'added'}.`
            });
            setEditingAch(null);
            loadAchievements();
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to save achievement.' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Delete achievement permanently?')) return;
        try {
            await api.adminDeleteAchievement(id);
            setFlashMessage({ type: 'success', text: 'Achievement deleted.' });
            loadAchievements();
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to delete achievement.' });
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">Key Honors & Achievements</h1>
                    <p className="text-sm text-slate-400 mt-1">Milestones in student governance, fiscal responsibility, and community programs.</p>
                </div>

                <button 
                    type="button" 
                    onClick={() => setEditingAch({ title: '', subtitle: '', year: new Date().getFullYear().toString(), badge: 'Milestone', is_featured: false, is_published: true, sort_order: 0 })}
                    className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg transition cursor-pointer"
                >
                    + Add Achievement
                </button>
            </div>

            {/* Modal */}
            {editingAch && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-lg w-full space-y-6 shadow-2xl">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold text-white">
                                {editingAch.id ? 'Edit Achievement' : 'Add Achievement'}
                            </h2>
                            <button 
                                type="button" 
                                onClick={() => setEditingAch(null)}
                                className="text-slate-400 hover:text-white text-xl"
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Title</label>
                                <input 
                                    type="text" 
                                    required
                                    value={editingAch.title || ''}
                                    onChange={e => setEditingAch({ ...editingAch, title: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                    placeholder="e.g. Student Leadership Honor"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Badge Label</label>
                                    <input 
                                        type="text" 
                                        value={editingAch.badge || ''}
                                        onChange={e => setEditingAch({ ...editingAch, badge: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                        placeholder="e.g. Milestone / Honor"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Year</label>
                                    <input 
                                        type="text" 
                                        value={editingAch.year || ''}
                                        onChange={e => setEditingAch({ ...editingAch, year: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                        placeholder="e.g. 2024"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Subtitle</label>
                                <input 
                                    type="text" 
                                    value={editingAch.subtitle || ''}
                                    onChange={e => setEditingAch({ ...editingAch, subtitle: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                    placeholder="e.g. Interact Club / High School Band"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Description</label>
                                <textarea 
                                    rows={3}
                                    value={editingAch.description || ''}
                                    onChange={e => setEditingAch({ ...editingAch, description: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                                />
                            </div>

                            <div className="flex flex-col gap-2 pt-2">
                                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={editingAch.is_featured ?? false}
                                        onChange={e => setEditingAch({ ...editingAch, is_featured: e.target.checked })}
                                        className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500" 
                                    />
                                    <span>Feature on homepage milestone grid</span>
                                </label>
                                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={editingAch.is_published ?? true}
                                        onChange={e => setEditingAch({ ...editingAch, is_published: e.target.checked })}
                                        className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500" 
                                    />
                                    <span>Achievement is active and published</span>
                                </label>
                            </div>

                            <div className="pt-4 border-t border-slate-800 flex items-center justify-end gap-3">
                                <button 
                                    type="button" 
                                    onClick={() => setEditingAch(null)}
                                    className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={saving}
                                    className="px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : 'Save Achievement'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Achievements Table */}
            <div className="p-6 rounded-3xl glass-card overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead>
                        <tr className="border-b border-slate-800 text-xs text-slate-400 uppercase tracking-wider">
                            <th className="py-4 font-semibold">Title</th>
                            <th className="py-4 font-semibold">Badge</th>
                            <th className="py-4 font-semibold">Year</th>
                            <th className="py-4 font-semibold">Status</th>
                            <th className="py-4 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {achievements.length > 0 ? (
                            achievements.map(ach => (
                                <tr key={ach.id} className="hover:bg-slate-800/20">
                                    <td className="py-4">
                                        <span className="font-bold text-white block">{ach.title}</span>
                                        {ach.subtitle && <span className="text-xs text-emerald-400">{ach.subtitle}</span>}
                                    </td>
                                    <td className="py-4 text-xs font-mono text-slate-300">{ach.badge || 'Milestone'}</td>
                                    <td className="py-4 text-xs text-slate-400">{ach.year || '—'}</td>
                                    <td className="py-4">
                                        {ach.is_published ? (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                Active
                                            </span>
                                        ) : (
                                            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-800 text-slate-400 border border-slate-700">
                                                Inactive
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-4 text-right space-x-2">
                                        <button 
                                            type="button" 
                                            onClick={() => setEditingAch(ach)}
                                            className="px-2.5 py-1 rounded text-xs bg-emerald-950 text-emerald-300 border border-emerald-800 hover:bg-emerald-900 transition cursor-pointer"
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={() => handleDelete(ach.id)}
                                            className="px-2.5 py-1 rounded text-xs bg-rose-950/60 text-rose-400 border border-rose-800/40 hover:bg-rose-900 transition cursor-pointer"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-slate-500">
                                    {loading ? 'Loading achievements...' : 'No achievements found.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
